import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Card, Col, Row } from 'reactstrap';
import Translate from '@securitize/ui-components/i18n/Translate/Translate';
import './DepositAddresses.scss';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { successToast } from '@securitize/ui-components/Toast';
import OpportunityCalculator from '../../../OpportunityCalculator';
import CustomCircleProgressBar from '../../../CustomCircleProgressBar';
import FundingInstructions from '../FundingInstructions';
import SanitizedReactHtmlParser from '../../../../../../../utils/helpers/SanitizedReactHtmlParser';

const DepositAddresses = ({
  opportunityId,
  addresses,
  onOpportunityUpdate,
  pageNumber,
  totalSteps,
  additionalAccreditation,
}) => {
  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }));
  const needsAccreditation = Boolean(additionalAccreditation) && user.accreditedStatus !== 'confirmed';
  return (
    <div className="deposit-addresses">
      <Row className="p-0 my-5 mx-0">
        <Col className="d-flex align-items-center p-0">
          <CustomCircleProgressBar
            currentValue={pageNumber}
            totalItems={totalSteps}
          />
          <h1 className="font-32 mb-0">
            {`0${pageNumber}. `}
            <Translate translationKey="Texts.payment" />
          </h1>
        </Col>
      </Row>
      {needsAccreditation && <FundingInstructions />}
      <Row className="d-flex justify-content-between flex-wrap-reverse mx-0">
        {!needsAccreditation && (
          <Col xs={12} md={8} className="deposit-addresses__col">
            <Card className="deposit-addresses__card h-100 border-0 box-shadow">
              {addresses.map(({ address, type }) => (
                <div key={address + type}>
                  <div className="d-flex justify-content-between align-items-center pb-3">
                    <h4 className="font-20 text-input">
                      <i className="icon-paper-clip mr-2" />
                      {type === 'fiat' ? (
                        <Translate translationKey="Texts.bank-account" />
                      ) : (
                        <Translate translationKey="Texts.crypto-address" />
                      )}
                    </h4>
                  </div>
                  {type !== 'fiat' && (
                    <span className="font-16 subtext pt-3 pb-3">
                      <Translate translationKey="Text.funds-address" />
                    </span>
                  )}
                  <div className="pt-3">
                    {type === 'fiat' ? (
                      <div className="font-16 p-4 deposit-addresses__bank-account">
                        {SanitizedReactHtmlParser(address)}
                      </div>
                    ) : (
                      <div className="form-control d-flex justify-content-between align-items-center font-16 p-4 input-text">
                        <span className="text-truncate">{address}</span>
                        <CopyToClipboard
                          text={address}
                          onCopy={() => successToast('copied to clipboard')}
                        >
                          <i className="icon-copy font-18" />
                        </CopyToClipboard>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </Col>
        )}
        <Col
          xs={12}
          md={needsAccreditation ? 8 : 4}
          className="deposit-addresses__calc-col p-0"
        >
          <Row>
            <Col xs={12} md={needsAccreditation ? 6 : 12}>
              <OpportunityCalculator
                editDisabled
                xs={12}
                md={12}
                opportunityId={opportunityId}
                onOpportunityUpdate={onOpportunityUpdate}
              />
            </Col>
            {!needsAccreditation && (
              <Col xs={12} md={needsAccreditation ? 6 : 12}>
                <div className="position-relative">
                  <Card className="deposit-addresses__confirmation-card p-2 border-0 box-shadow">
                    <Col>
                      <span className="font-14 input-text">
                        <Translate translationKey="Texts.avg-time-payment" />
                      </span>
                    </Col>
                    <Col className="d-flex align-items-center pt-4 pb-4">
                      <i className="icon-clock mr-2 font-24" />
                      <span className="font-20 font-weight-bolder">
                        <Translate translationKey="Texts.processing-time" />
                      </span>
                    </Col>
                    <Col>
                      <span className="subtext font-14">
                        <Translate translationKey="Texts.we-send-confirmation" />
                      </span>
                    </Col>
                  </Card>
                </div>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

DepositAddresses.defaultProps = {
  addresses: [],
  onOpportunityUpdate: () => undefined,
};

DepositAddresses.propTypes = {
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  addresses: PropTypes.arrayOf(PropTypes.any),
  onOpportunityUpdate: PropTypes.func,
  pageNumber: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  additionalAccreditation: PropTypes.bool.isRequired,
};

export default DepositAddresses;
